import React, { useState } from 'react';
import image from '../data/other/about1.jpg';
import image2 from '../data/other/pexels-cmrcn-29224591.jpg';
import image3 from '../data/other/9thimage.jpg';
import Parlexeffect from '../components/Core/Home/Parlexeffect';
import CountUp from 'react-countup';
import ScrollTrigger from 'react-scroll-trigger';
import { motion } from 'framer-motion';

// Motion Variants for Animation Reuse
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const floatAnim = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      ease: 'easeInOut',
      repeat: Infinity,
    },
  },
};

const About = () => {
  const [counterOn, setCounterOn] = useState(false);

  return (
    <div className="overflow-hidden bg-gradient-to-b from-white via-blue-50 to-blue-100 relative">
      {/* Background Floating Shapes */}
      <motion.div
        className="absolute top-10 left-10 w-24 h-24 rounded-full bg-blue-300 opacity-30 blur-2xl"
        {...floatAnim}
      />
      <motion.div
        className="absolute bottom-10 right-10 w-32 h-32 rounded-full bg-emerald-300 opacity-30 blur-3xl"
        {...floatAnim}
      />

      {/* Hero Section */}
      <div className="w-full min-h-[400px] flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950 px-4 md:px-10 py-20 relative overflow-hidden">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          className="w-full max-w-2xl text-center z-10"
        >
          <motion.h2
            whileHover={{ scale: 1.05 }}
            className="font-bold text-lg md:text-xl text-indigo-300 tracking-widest uppercase"
          >
            About Us
          </motion.h2>
          <motion.h1
            whileHover={{ scale: 1.03 }}
            className="font-bold text-2xl md:text-4xl text-white mt-5 leading-snug"npm
          >
            MD CONTROL AND IT SOLUTIONS was established in 2025 .
          </motion.h1>
          <motion.p
            className="font-medium text-sm md:text-lg text-slate-300 mt-6"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            Embark on a transformative journey into the heart of Industry 4.0 with our revolutionary industry automation.
          </motion.p>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
      </div>

      {/* Image Section */}
      <div className="w-full min-h-[400px] flex justify-center items-center py-10">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
          whileHover={{ scale: 1.03 }}
          className="relative w-full max-w-md md:max-w-2xl bg-gradient-to-tr from-blue-100 to-emerald-50 rounded-3xl mx-4 md:mx-10 lg:mx-14 p-4 shadow-2xl overflow-hidden group"
        >
          <img
            src={image}
            alt=""
            className="w-full h-auto rounded-3xl group-hover:scale-110 transition-transform duration-700"
          />
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-blue-500/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
        </motion.div>
      </div>

      {/* Part 2 - Our Story */}
      <div className="w-full p-6 md:p-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Text Section */}
          <motion.div
            variants={fadeInLeft}
            initial="hidden"
            whileInView="visible"
            className="w-full"
          >
            <h1 className="font-bold text-xl md:text-3xl text-blue-900 mb-4 md:mb-8">Our Story</h1>
            <h2 className="text-lg md:text-2xl lg:text-4xl font-medium leading-snug">
              Welcome to MD CONTROL AND IT SOLUTIONS, where our story in industry automation is one of innovation and transformative solutions since 2025.
            </h2>
            <ul className="flex flex-col gap-4 mt-6 md:mt-8 text-blue-950">
              <li className="font-semibold leading-relaxed relative pl-6 before:content-['▹'] before:absolute before:left-0 before:text-blue-600">
                Started with PLC Programming, HMI Design, and SCADA Programming before expanding into special-purpose machine manufacturing and IT solutions.
              </li>
              <li className="font-semibold leading-relaxed relative pl-6 before:content-['▹'] before:absolute before:left-0 before:text-blue-600">
                Created a vertical to supply special-size & special composition quality to customers who struggle to find the right materials.
              </li>
            </ul>
          </motion.div>

          {/* Image Section */}
          <motion.div
            variants={fadeInRight}
            initial="hidden"
            whileInView="visible"
            className="w-full flex justify-center md:justify-end"
          >
            <motion.img
              src={image2}
              alt="image2"
              className="w-full max-w-xs sm:max-w-md md:max-w-lg rounded-3xl shadow-2xl hover:scale-105 transition-transform duration-700"
              whileHover={{ rotate: [0, 1, -1, 0], transition: { duration: 1 } }}
            />
          </motion.div>
        </div>
      </div>

      {/* Infra and Members Section */}
      <motion.div
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        className="w-full flex flex-col gap-y-6 justify-center items-center mt-10 bg-gradient-to-br from-blue-100 to-blue-200 p-8 rounded-3xl shadow-inner"
      >
        <motion.h1
          whileHover={{ scale: 1.05 }}
          className="font-extrabold text-3xl md:text-4xl text-blue-900 text-center"
        >
          About Our Infrastructure & Members
        </motion.h1>

        <motion.ul
          variants={fadeInUp}
          className="flex flex-col gap-y-6 font-semibold text-blue-800 max-w-3xl text-center md:text-left"
        >
          <li>📍 Located at C-115A, Mohan Garden, New Delhi</li>
          <li>👷 We have 8 manpower in our factory and 3 office staff</li>
          <li>🎯 All team members are technically experienced and professionally skilled</li>
          <li>💡 Our main motto is customer satisfaction through quality products and timely delivery</li>
        </motion.ul>
      </motion.div>

      {/* Animated Counters */}
      <ScrollTrigger onEnter={() => setCounterOn(true)} onExit={() => setCounterOn(false)}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="mt-10 w-full h-auto py-10 bg-gradient-to-r from-slate-900 to-black mb-10 flex flex-wrap items-center justify-evenly gap-8 shadow-inner"
        >
          {[
            { end: 13, suffix: '+', label: 'Years Experience' },
            { end: 15, suffix: 'K+', label: 'Positive Reviews' },
            { end: 13, suffix: 'K+', label: 'Global Customers' },
            { end: 99, suffix: '%', label: 'Return on Investment' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              transition={{ delay: i * 0.2 }}
              className="text-center"
            >
              <motion.div whileHover={{ scale: 1.1 }} className="flex justify-center items-baseline">
                <h1 className="text-white lg:text-5xl font-extrabold">
                  {counterOn && <CountUp start={0} end={stat.end} duration={5} delay={0} />}
                </h1>
                <span className="text-blue-500 lg:text-5xl font-extrabold">{stat.suffix}</span>
              </motion.div>
              <p className="text-white mt-2 text-lg font-medium">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </ScrollTrigger>

      {/* Vision & Mission */}
      <div className="w-full flex flex-col md:flex-row items-center gap-10 p-6 md:p-12">
        {/* Image Section */}
        <motion.div
          variants={fadeInLeft}
          initial="hidden"
          whileInView="visible"
          className="w-full max-w-xs sm:max-w-md md:max-w-lg"
        >
          <motion.img
            src={image3}
            alt="img10"
            className="w-full h-auto rounded-3xl shadow-2xl hover:scale-105 transition-all duration-700"
            whileHover={{ y: -10 }}
          />
        </motion.div>

        {/* Text Section */}
        <motion.div
          variants={fadeInRight}
          initial="hidden"
          whileInView="visible"
          className="w-full max-w-lg flex flex-col gap-6 text-center md:text-left"
        >
          <h2 className="font-semibold text-lg md:text-2xl text-blue-800">Our Vision & Mission</h2>
          <h1 className="text-xl md:text-3xl font-bold text-black">
            Our Main Motto Is Customer Satisfaction With Our Quality Product And Timely Delivery
          </h1>

          <ul className="flex flex-col gap-6">
            {[
              {
                title: 'Forefront of the Automation Industry',
                desc: 'We are committed to continuous innovation, driving automation to create smarter industrial ecosystems.',
              },
              {
                title: 'Empowering Businesses with Innovation',
                desc: 'We integrate technology and creativity to empower businesses with intelligent and efficient solutions.',
              },
              {
                title: 'Trailblazing the Automation Frontier',
                desc: 'Leading the charge into a smarter future with robotics, AI, and IoT-driven industrial automation.',
              },
            ].map((point, index) => (
              <motion.li
                key={index}
                whileHover={{ scale: 1.02, x: 5 }}
                className="flex items-start gap-3"
              >
                <span className="relative flex h-4 w-4 mt-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-sky-500"></span>
                </span>
                <div>
                  <p className="text-lg md:text-xl font-bold">{point.title}</p>
                  <p className="text-sm md:text-base text-gray-700">{point.desc}</p>
                </div>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* Parallax Section */}
      <Parlexeffect />
    </div>
  );
};

export default About;
